import { useParams, Navigate } from "react-router-dom";
import { CalendarCheck, UtensilsCrossed, BedDouble, Home as HomeIcon, Star, Speaker, Wifi, Armchair } from "lucide-react";
import { useLang } from "@/App";
import { all, pick } from "@/data/images";
import { waLink, TEL1, EMAIL_RESERVAS } from "@/data/site";
import Hero from "@/components/Hero";
import { Reveal, Pretitulo, BtnPrimario } from "@/components/Reveal";

const SERV_ICONOS = [HomeIcon, UtensilsCrossed, CalendarCheck, BedDouble, Star];
const EQ_ICONOS = [Armchair, Wifi, Speaker];
// Foco (object-position) por tipo para evitar cortar cabezas en pantallas anchas.
const HERO_FOCO = { empresariales: "center 30%" };

export default function EventoTipo() {
  const { tipo } = useParams();
  const { t, L } = useLang();
  const p = t.eventoTipo[tipo];
  if (!p) return <Navigate to={L("eventos")} replace />;

  const g = t.global.footer;
  const cotizarLink = waLink(`${t.nav.reservar} — ${p.hero.titulo}: `);

  return (
    <div data-testid={`pagina-eventos-${tipo}`}>
      <Hero pre={p.hero.pre} titulo={p.hero.titulo} fotos={[pick(p.hero.foto[0], 0) || all(p.hero.foto[0])[0]]} foco={HERO_FOCO[tipo]} testid={`hero-eventos-${tipo}`} />

      {/* 2. Presentación + Informes & Reservas */}
      <section className="py-20 md:py-28" data-testid={`et-presentacion-${tipo}`}>
        <div className="mx-auto grid max-w-[1200px] gap-12 px-6 md:px-10 lg:grid-cols-[7fr_4fr] lg:gap-20">
          <Reveal>
            <Pretitulo center={false}>{p.presentacion.pre}</Pretitulo>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink md:text-5xl">{p.presentacion.titulo}</h2>
            <p className="mt-7 max-w-2xl text-[15.5px] leading-relaxed text-ink-soft">{p.presentacion.p1}</p>
            {p.presentacion.p2 && <p className="mt-4 max-w-2xl text-[15.5px] leading-relaxed text-ink-soft">{p.presentacion.p2}</p>}
          </Reveal>
          <Reveal delay={0.12} className="hidden lg:block">
            <div className="border-t-2 border-terra bg-sand-deep p-8 md:p-9" data-testid={`et-informes-${tipo}`}>
              <p className="text-[11px] font-bold tracking-[0.24em] text-terra">{g.informes}</p>
              <ul className="mt-6 space-y-4 text-sm">
                <li>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-mute">{g.telefono}</p>
                  <a href={`tel:${TEL1.replace(/\s/g, "")}`} className="mt-1 block font-semibold text-ink hover:text-terra" data-testid={`et-telefono-${tipo}`}>
                    {TEL1}
                  </a>
                </li>
                <li>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-mute">{g.correo}</p>
                  <a href={`mailto:${EMAIL_RESERVAS}`} className="mt-1 block break-all font-semibold text-ink hover:text-terra" data-testid={`et-email-${tipo}`}>
                    {EMAIL_RESERVAS}
                  </a>
                </li>
              </ul>
              <div className="mt-7">
                <BtnPrimario href={cotizarLink} testid={`et-cotizar-btn-${tipo}`}>{g.cotizar}</BtnPrimario>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 3. Servicios */}
      <section className="bg-sand-deep py-20 md:py-28" data-testid={`et-servicios-${tipo}`}>
        <div className="mx-auto max-w-3xl px-6 md:px-10">
          <div className="space-y-0">
            {p.servicios.map((s, i) => {
              const Icono = SERV_ICONOS[i] || HomeIcon;
              return (
                <Reveal key={s.t} delay={i * 0.05} className="flex items-start gap-6 border-b border-lineas py-7 first:border-t" testid={`et-servicio-${tipo}-${i}`}>
                  <Icono size={24} strokeWidth={1.3} className="mt-0.5 shrink-0 text-terra" />
                  <div>
                    <h3 className="font-serif text-xl text-ink">{s.t}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{s.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. Equipo técnico */}
      <section className="py-20 md:py-28" data-testid={`et-equipo-${tipo}`}>
        <div className="mx-auto max-w-3xl px-6 md:px-10">
          <div className="space-y-0">
            {p.equipo.map((s, i) => {
              const Icono = EQ_ICONOS[i] || Armchair;
              return (
                <Reveal key={s.t} delay={i * 0.05} className="flex items-start gap-6 border-b border-lineas py-7 first:border-t" testid={`et-equipo-${tipo}-${i}`}>
                  <Icono size={24} strokeWidth={1.3} className="mt-0.5 shrink-0 text-ochre" />
                  <div>
                    <h3 className="font-serif text-xl text-ink">{s.t}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{s.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}